import { createFormatter, hasLocale } from 'next-intl'

import { formats } from './formats'
import { defaultLocale, locales } from './locales'

type LocaleFormatter = ReturnType<typeof createFormatter>

/**
 * One formatter per locale, built once. `createFormatter` wraps the `Intl` constructors
 * behind a cache, so sharing the instance is what stops a page from rebuilding an
 * `Intl.NumberFormat` for every fare it renders.
 *
 * Bounded by the served-locale set, so this never grows: it is keyed on whatever string
 * a caller passes rather than on `Locale` only because the copy pipeline threads locales
 * through as plain strings.
 */
const formatters = new Map<string, LocaleFormatter>()

/**
 * The app's shared next-intl formatter for a locale — the one stack every rendered
 * number, price and date goes through, so a locale's conventions are decided in one
 * place rather than at each call site's `Intl` invocation.
 *
 * Usable from plain library code, server components and client components alike: it takes
 * the locale as an argument rather than reading React context, so a transform running
 * outside the render tree formats exactly as a component does. Components get the locale
 * from `useLocale()` and hand it here.
 *
 * **`dateTime` is pinned to UTC** (→ invariants.md#ssr-safe-rendered-dates). Every date
 * this app renders is a calendar date the warehouse or the URL already fixed, never an
 * instant to project into the reader's zone, so the caller builds it from explicit Y/M/D
 * parts at UTC (`Date.UTC(...)`) and reads it back in UTC. Leaving the zone implicit would
 * take the *process* zone instead, making SSR and hydration disagree — a React #418.
 */
export function localeFormatter(locale: string): LocaleFormatter {
    // Total over whatever string reaches it: a locale the app does not serve falls back
    // to the default rather than reaching `Intl`, which throws a `RangeError` on a
    // malformed tag. A render path may not fail over a locale it could not parse.
    const served = hasLocale(locales, locale) ? locale : defaultLocale

    let formatter = formatters.get(served)
    if (!formatter) {
        formatter = createFormatter({ locale: served, formats, timeZone: 'UTC' })
        formatters.set(served, formatter)
    }
    return formatter
}
